/**
 * Symbol normalization for tickers entering Hearth from Merrill exports
 * (or anywhere else with the same broker-side conventions). Tiingo's
 * canonical form differs from Merrill's for a small number of symbols;
 * we canonicalize at every entry point — Merrill XLSX/CSV import, the
 * /alerts rule-create form, the /watchlists add-ticker flow — so the
 * `prices` table only ever gets the Tiingo form.
 *
 * New entries land here as we discover them. Keep the map small;
 * pattern-based normalization (e.g. ".B" → "-B") is tempting but
 * brittle — Tiingo's actual divergences aren't a single pattern.
 *
 * Lifted from `jobs/src/lib/merrill-parser.ts` to `shared/` in Phase
 * 15.5 so the API handlers can call `normalizeTicker()` at rule-create
 * time without web/jobs/api package boundary issues.
 */

export const MERRILL_TICKER_MAP: Readonly<Record<string, string>> =
  Object.freeze({
    // Berkshire B-class shares: Tiingo uses BRK-B; Merrill uses BRKB.
    BRKB: "BRK-B",
    // RDS-A / RDS-B (Royal Dutch Shell) renamed to SHEL on consolidation;
    // Merrill exports already use SHEL. No mapping needed.
  });

/**
 * Uppercase the input, strip whitespace, then look up in the map. If
 * the input matches a Merrill-side key, return the Tiingo form;
 * otherwise return the cleaned input. Never throws — empty input
 * returns empty string and lets the caller's validator decide.
 */
export function normalizeTicker(raw: string): string {
  const cleaned = raw.trim().toUpperCase();
  return MERRILL_TICKER_MAP[cleaned] ?? cleaned;
}
